/**
 * AppRouter — lazy-loads all screen-level components.
 * Rule 5: Lazy load all screen-level components — never eager-import page components.
 *
 * Every route element is wrapped in a RouteErrorBoundary so a crash in one
 * screen never produces a blank page — it shows the fallback with a Sentry
 * route tag for triage. The top-level <ErrorBoundary route="root"> in
 * main.tsx still catches provider-level failures.
 */
import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ProtectedRoute }     from '@/components/ProtectedRoute'
import { AppShell }           from '@/components/AppShell'
import { LoadingScreen }      from '@/components/LoadingScreen'
import { RouteErrorBoundary } from '@/components/ErrorBoundary'
import { ROUTES }             from '@/lib/routes'

// ── Public marketing ─────────────────────────────────────────────────────────
const Landing        = lazy(() => import('@/pages/Landing'))
const Privacy        = lazy(() => import('@/pages/Privacy'))
const Terms          = lazy(() => import('@/pages/Terms'))
const Contact        = lazy(() => import('@/pages/Contact'))
const FAQ            = lazy(() => import('@/pages/FAQ'))

// ── Auth screens (public) ─────────────────────────────────────────────────────
const Login          = lazy(() => import('@/pages/auth/Login'))
const Signup         = lazy(() => import('@/pages/auth/Signup'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const AuthCallback   = lazy(() => import('@/pages/auth/Callback'))
const AuthRedeem     = lazy(() => import('@/pages/auth/Redeem'))
const ResetPassword  = lazy(() => import('@/pages/auth/ResetPassword'))
const ConsentVerify  = lazy(() => import('@/pages/auth/ConsentVerify'))

// ── Auth-gated (no AppShell) ──────────────────────────────────────────────────
const Onboarding      = lazy(() => import('@/pages/Onboarding'))
const Diagnostic      = lazy(() => import('@/pages/Diagnostic'))
const Paywall         = lazy(() => import('@/pages/Paywall'))
const SubSuccess      = lazy(() => import('@/pages/SubscriptionSuccess'))
const PendingConsent  = lazy(() => import('@/pages/PendingConsent'))
const ParentDashboard = lazy(() => import('@/pages/ParentDashboard'))
const ParentBilling       = lazy(() => import('@/pages/ParentBilling'))
const CancellationFlow    = lazy(() => import('@/pages/CancellationFlow'))

// ── Auth + subscription (inside AppShell) ────────────────────────────────────
const Home     = lazy(() => import('@/pages/Home'))
const Practice = lazy(() => import('@/pages/Practice'))
const Quiz     = lazy(() => import('@/pages/Quiz'))
const Results  = lazy(() => import('@/pages/Results'))
const Progress = lazy(() => import('@/pages/Progress'))
const Settings = lazy(() => import('@/pages/Settings'))

// ── AP exam prep — public entry points ───────────────────────────────────────
const APLanding            = lazy(() => import('@/ap/pages/APLanding'))
const APLogin              = lazy(() => import('@/ap/pages/APLogin'))
const APSignup             = lazy(() => import('@/ap/pages/APSignup'))

// ── AP exam prep module (auth-gated, no K-12 subscription required) ──────────
const APHub                = lazy(() => import('@/ap/pages/APHub'))
const APPaywall            = lazy(() => import('@/ap/pages/APPaywall'))
const APPractice           = lazy(() => import('@/ap/pages/APPractice'))
const APResults            = lazy(() => import('@/ap/pages/APResults'))
const APProgress           = lazy(() => import('@/ap/pages/APProgress'))
const APDiagnostic         = lazy(() => import('@/ap/pages/APDiagnostic'))
const APDiagnosticResults  = lazy(() => import('@/ap/pages/APDiagnosticResults'))
const APFRQPractice        = lazy(() => import('@/ap/pages/APFRQPractice'))
const APStudyPlan          = lazy(() => import('@/ap/pages/APStudyPlan'))
const APBilling            = lazy(() => import('@/ap/pages/APBilling'))
const APMockExam           = lazy(() => import('@/ap/pages/APMockExam'))
const APReviewQueue        = lazy(() => import('@/ap/pages/APReviewQueue'))
const APVocabPractice      = lazy(() => import('@/ap/pages/APVocabPractice'))

// ── Catch-all ─────────────────────────────────────────────────────────────────
const NotFound = lazy(() => import('@/pages/NotFound'))

// ─────────────────────────────────────────────────────────────────────────────

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ── Public landing ───────────────────────────────────────────────── */}
        <Route path={ROUTES.LANDING} element={
          <RouteErrorBoundary route={ROUTES.LANDING}><Landing /></RouteErrorBoundary>
        } />

        {/* ── Public legal ─────────────────────────────────────────────────── */}
        <Route path={ROUTES.PRIVACY} element={
          <RouteErrorBoundary route={ROUTES.PRIVACY}><Privacy /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.TERMS} element={
          <RouteErrorBoundary route={ROUTES.TERMS}><Terms /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.CONTACT} element={
          <RouteErrorBoundary route={ROUTES.CONTACT}><Contact /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.FAQ} element={
          <RouteErrorBoundary route={ROUTES.FAQ}><FAQ /></RouteErrorBoundary>
        } />

        {/* ── Public auth routes ───────────────────────────────────────────── */}
        <Route path={ROUTES.LOGIN} element={
          <RouteErrorBoundary route={ROUTES.LOGIN}><Login /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.SIGNUP} element={
          <RouteErrorBoundary route={ROUTES.SIGNUP}><Signup /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.FORGOT} element={
          <RouteErrorBoundary route={ROUTES.FORGOT}><ForgotPassword /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AUTH_CALLBACK} element={
          <RouteErrorBoundary route={ROUTES.AUTH_CALLBACK}><AuthCallback /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AUTH_REDEEM} element={
          <RouteErrorBoundary route={ROUTES.AUTH_REDEEM}><AuthRedeem /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AUTH_RESET} element={
          <RouteErrorBoundary route={ROUTES.AUTH_RESET}><ResetPassword /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AUTH_CONSENT} element={
          <RouteErrorBoundary route={ROUTES.AUTH_CONSENT}><ConsentVerify /></RouteErrorBoundary>
        } />

        {/* ── Auth-gated, no AppShell ──────────────────────────────────────── */}
        <Route path={ROUTES.ONBOARDING} element={
          <RouteErrorBoundary route={ROUTES.ONBOARDING}>
            <ProtectedRoute requireSubscription={false}>
              <Onboarding />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.DIAGNOSTIC} element={
          <RouteErrorBoundary route={ROUTES.DIAGNOSTIC}>
            <ProtectedRoute requireSubscription={false}>
              <Diagnostic />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.PAYWALL} element={
          <RouteErrorBoundary route={ROUTES.PAYWALL}>
            <ProtectedRoute requireSubscription={false}>
              <Paywall />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.SUB_SUCCESS} element={
          <RouteErrorBoundary route={ROUTES.SUB_SUCCESS}>
            <ProtectedRoute requireSubscription={false}>
              <SubSuccess />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.PENDING_CONSENT} element={
          <RouteErrorBoundary route={ROUTES.PENDING_CONSENT}>
            <ProtectedRoute requireSubscription={false}>
              <PendingConsent />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />

        {/* ── Auth-gated — parent dashboard (no AppShell, no BottomNav) ────── */}
        {/* requireSubscription=true: parents with expired trial are redirected  */}
        {/* to /paywall. PARENT_BILLING stays false so lapsed parents can still  */}
        {/* manage their billing/update payment without needing an active sub.   */}
        <Route path={ROUTES.PARENT} element={
          <RouteErrorBoundary route={ROUTES.PARENT}>
            <ProtectedRoute requireSubscription={true} requireRole="parent">
              <ParentDashboard />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />

        {/* ── Parent billing portal redirect ────────────────────────────────── */}
        {/* Linked from payment-failed + subscription-canceled emails.           */}
        {/* Calls create-portal edge fn → bounces to Stripe Customer Portal.    */}
        <Route path={ROUTES.PARENT_BILLING} element={
          <RouteErrorBoundary route={ROUTES.PARENT_BILLING}>
            <ProtectedRoute requireSubscription={false}>
              <ParentBilling />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />

        {/* ── Cancellation flow (survey + pause offer) ─────────────────────── */}
        {/* Active-subscriber parents only — non-active profiles redirect to    */}
        {/* /settings via in-component guard.                                   */}
        <Route path={ROUTES.PARENT_CANCEL} element={
          <RouteErrorBoundary route={ROUTES.PARENT_CANCEL}>
            <ProtectedRoute requireSubscription={false}>
              <CancellationFlow />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />

        {/* ── Auth + subscription — inside AppShell layout ─────────────────── */}
        <Route element={
          <ProtectedRoute requireSubscription={true}>
            <AppShell />
          </ProtectedRoute>
        }>
          <Route path={ROUTES.HOME} element={
            <RouteErrorBoundary route={ROUTES.HOME}><Home /></RouteErrorBoundary>
          } />
          <Route path={ROUTES.PRACTICE} element={
            <RouteErrorBoundary route={ROUTES.PRACTICE}><Practice /></RouteErrorBoundary>
          } />
          <Route path={ROUTES.QUIZ} element={
            <RouteErrorBoundary route={ROUTES.QUIZ}><Quiz /></RouteErrorBoundary>
          } />
          <Route path={ROUTES.RESULTS} element={
            <RouteErrorBoundary route={ROUTES.RESULTS}><Results /></RouteErrorBoundary>
          } />
          <Route path={ROUTES.PROGRESS} element={
            <RouteErrorBoundary route={ROUTES.PROGRESS}><Progress /></RouteErrorBoundary>
          } />
          <Route path={ROUTES.SETTINGS} element={
            <RouteErrorBoundary route={ROUTES.SETTINGS}><Settings /></RouteErrorBoundary>
          } />
        </Route>

        {/* ── AP exam prep — public entry points ──────────────────────────── */}
        <Route path={ROUTES.AP_LANDING} element={
          <RouteErrorBoundary route={ROUTES.AP_LANDING}><APLanding /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_LOGIN} element={
          <RouteErrorBoundary route={ROUTES.AP_LOGIN}><APLogin /></RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_SIGNUP} element={
          <RouteErrorBoundary route={ROUTES.AP_SIGNUP}><APSignup /></RouteErrorBoundary>
        } />

        {/* ── AP exam prep — auth-gated, no K-12 subscription required ────── */}
        <Route path={ROUTES.AP_HUB} element={
          <RouteErrorBoundary route={ROUTES.AP_HUB}>
            <ProtectedRoute requireSubscription={false}>
              <APHub />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_PAYWALL} element={
          <RouteErrorBoundary route={ROUTES.AP_PAYWALL}>
            <ProtectedRoute requireSubscription={false}>
              <APPaywall />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_PRACTICE} element={
          <RouteErrorBoundary route={ROUTES.AP_PRACTICE}>
            <ProtectedRoute requireSubscription={false}>
              <APPractice />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_RESULTS} element={
          <RouteErrorBoundary route={ROUTES.AP_RESULTS}>
            <ProtectedRoute requireSubscription={false}>
              <APResults />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_PROGRESS} element={
          <RouteErrorBoundary route={ROUTES.AP_PROGRESS}>
            <ProtectedRoute requireSubscription={false}>
              <APProgress />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_DIAGNOSTIC} element={
          <RouteErrorBoundary route={ROUTES.AP_DIAGNOSTIC}>
            <ProtectedRoute requireSubscription={false}>
              <APDiagnostic />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_DIAGNOSTIC_RESULTS} element={
          <RouteErrorBoundary route={ROUTES.AP_DIAGNOSTIC_RESULTS}>
            <ProtectedRoute requireSubscription={false}>
              <APDiagnosticResults />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_FRQ} element={
          <RouteErrorBoundary route={ROUTES.AP_FRQ}>
            <ProtectedRoute requireSubscription={false}>
              <APFRQPractice />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_PLAN} element={
          <RouteErrorBoundary route={ROUTES.AP_PLAN}>
            <ProtectedRoute requireSubscription={false}>
              <APStudyPlan />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_BILLING} element={
          <RouteErrorBoundary route={ROUTES.AP_BILLING}>
            <ProtectedRoute requireSubscription={false}>
              <APBilling />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_MOCK_EXAM} element={
          <RouteErrorBoundary route={ROUTES.AP_MOCK_EXAM}>
            <ProtectedRoute requireSubscription={false}>
              <APMockExam />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_REVIEW_QUEUE} element={
          <RouteErrorBoundary route={ROUTES.AP_REVIEW_QUEUE}>
            <ProtectedRoute requireSubscription={false}>
              <APReviewQueue />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />
        <Route path={ROUTES.AP_VOCAB} element={
          <RouteErrorBoundary route={ROUTES.AP_VOCAB}>
            <ProtectedRoute requireSubscription={false}>
              <APVocabPractice />
            </ProtectedRoute>
          </RouteErrorBoundary>
        } />

        {/* ── Catch-all — accessible without auth (Rule 9) ─────────────────── */}
        <Route path={ROUTES.NOT_FOUND} element={
          <RouteErrorBoundary route={ROUTES.NOT_FOUND}><NotFound /></RouteErrorBoundary>
        } />
      </Routes>
    </Suspense>
  )
}
